<template>
  <main-template>
    <v-row>
      <v-col>
        <span class="text-h4">Diário de Atendimentos</span>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0 pt-0">
        <div class="d-flex justify-space-between mb-2">
          <v-btn
            v-if="$can(['atendimentos.criar', 'atendimentos.*'])"
            id="atendimentos.criarBtn"
            color="primary"
            dark
            @click="criar"
          >
            <v-icon left small>mdi-plus</v-icon>
            Criar atendimento
          </v-btn>
          <span v-else></span>

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            class="ml-2 search-input"
            dense
            hide-details
            label="Pesquisar por tipo, curso ou orientador..."
            single-line
            solo
            @keyup.enter="listar"
            @click:append="listar"
          />
        </div>
      </v-col>

      <v-col class="pt-0">
        <v-data-table
          :headers="tableHeaders"
          :items="atendimentos"
          :items-per-page="10"
          :loading="isLoading"
          class="elevation-1"
          no-data-text="Nenhum atendimento encontrado"
        >
          <template v-slot:[`item.tipo`]="{ item }">
            {{ item.tipo_atendimento ? item.tipo_atendimento.descricao : "- - -" }}
          </template>

          <template v-slot:[`item.publico_alvo`]="{ item }">
            <v-chip
              v-for="p in item.publico_alvo || []"
              :key="p"
              x-small
              outlined
              class="mr-1"
              color="primary"
            >
              {{ p }}
            </v-chip>
          </template>

          <template v-slot:[`item.participantes_count`]="{ item }">
            {{ item.participantes_count != null ? item.participantes_count : 0 }}
          </template>

          <template v-slot:[`item.presencas_confirmadas`]="{ item }">
            <v-chip small :color="item.presencas_confirmadas ? 'green' : 'grey lighten-1'" dark>
              <v-icon left x-small>
                {{ item.presencas_confirmadas ? "mdi-check" : "mdi-clock-outline" }}
              </v-icon>
              {{ item.presencas_confirmadas ? "Confirmada" : "Pendente" }}
            </v-chip>
          </template>

          <template v-slot:[`item.situacao`]="{ item }">
            <v-chip
              small
              :color="item.situacao === 'Confirmado' ? 'primary' : 'orange lighten-1'"
              dark
            >
              <v-icon left x-small>
                {{
                  item.situacao === "Confirmado" ? "mdi-check-decagram" : "mdi-clock-alert-outline"
                }}
              </v-icon>
              {{ item.situacao }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="grey darken-1"
                  x-small
                  dark
                  @click="visualizar(item)"
                >
                  <v-icon small>fa fa-eye</v-icon>
                </v-btn>
              </template>
              <span>Visualizar</span>
            </v-tooltip>

            <v-tooltip bottom v-if="$can(['atendimentos.imprimir', 'atendimentos.*'])">
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="deep-orange lighten-1"
                  x-small
                  dark
                  @click="imprimir(item)"
                >
                  <v-icon small>fa fa-file-pdf</v-icon>
                </v-btn>
              </template>
              <span>Imprimir PDF</span>
            </v-tooltip>

            <v-tooltip
              bottom
              v-if="
                $can(['atendimentos.confirmar', 'atendimentos.*']) && item.situacao !== 'Confirmado'
              "
            >
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="green"
                  x-small
                  dark
                  @click="confirmar(item)"
                >
                  <v-icon small>mdi-check-circle</v-icon>
                </v-btn>
              </template>
              <span>Confirmar atendimento</span>
            </v-tooltip>

            <v-tooltip bottom v-if="$can(['atendimentos.anexarCertificado', 'atendimentos.*'])">
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="blue-grey"
                  x-small
                  dark
                  @click="abrirCertificado(item)"
                >
                  <v-icon small>fa fa-paperclip</v-icon>
                </v-btn>
              </template>
              <span>Anexar certificado</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.certificado">
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="indigo"
                  x-small
                  dark
                  @click="baixarCertificado(item)"
                >
                  <v-icon small>fa fa-download</v-icon>
                </v-btn>
              </template>
              <span>Baixar certificado</span>
            </v-tooltip>

            <v-tooltip
              bottom
              v-if="$can(['atendimentos.editar', 'atendimentos.*', 'atendimentoFrequencia.*'])"
            >
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="blue darken-1"
                  dark
                  x-small
                  @click="frequenciaManual(item)"
                >
                  <v-icon small>mdi mdi-book-edit</v-icon>
                </v-btn>
              </template>
              <span>Frequência Manual</span>
            </v-tooltip>

            <v-tooltip
              bottom
              v-if="podeEditar(item) && $can(['atendimentos.editar', 'atendimentos.*'])"
            >
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="blue-grey lighten-3"
                  x-small
                  @click="editar(item)"
                >
                  <v-icon small>fa fa-edit</v-icon>
                </v-btn>
              </template>
              <span>Editar atendimento</span>
            </v-tooltip>

            <v-tooltip
              bottom
              color="error"
              v-if="podeEditar(item) && $can(['atendimentos.deletar', 'atendimentos.*'])"
            >
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="error"
                  x-small
                  @click="deletar(item)"
                >
                  <v-icon small>fa fa-trash</v-icon>
                </v-btn>
              </template>
              <span>Apagar atendimento</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Modal Visualizar -->
    <v-dialog v-model="dialogVisualizar" max-width="760" scrollable>
      <v-card v-if="detalhe">
        <v-card-title class="primary white--text">
          Atendimento — {{ detalhe.tipo_atendimento ? detalhe.tipo_atendimento.descricao : "" }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="font-weight-bold">Data</td>
                  <td>{{ detalhe.data_formatada }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Horário</td>
                  <td>{{ detalhe.horario }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Zona</td>
                  <td>{{ capitalize(detalhe.zona) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Público alvo</td>
                  <td>{{ (detalhe.publico_alvo || []).join(", ") }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Prof. Orientador</td>
                  <td>{{ detalhe.professor_orientador }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Carga Horária</td>
                  <td>{{ detalhe.carga_horaria }}</td>
                </tr>
                <tr v-if="detalhe.curso">
                  <td class="font-weight-bold">Curso</td>
                  <td>{{ detalhe.curso }}</td>
                </tr>
                <tr v-if="detalhe.disciplinas">
                  <td class="font-weight-bold">Disciplina(s)</td>
                  <td>{{ detalhe.disciplinas }}</td>
                </tr>
                <tr v-if="detalhe.habilidades">
                  <td class="font-weight-bold">Habilidades</td>
                  <td>{{ detalhe.habilidades }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Conteúdo</td>
                  <td style="white-space: pre-wrap">{{ detalhe.conteudo_ministrado }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div class="text-subtitle-1 font-weight-bold mt-4 mb-1">Frequência</div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Participante</th>
                  <th>Tipo</th>
                  <th class="text-center">PcD</th>
                  <th class="text-center">Presença</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!(detalhe.participantes && detalhe.participantes.length)">
                  <td colspan="4" class="text-center grey--text">Nenhum participante</td>
                </tr>
                <tr v-for="p in detalhe.participantes || []" :key="p.id">
                  <td>{{ nomeParticipante(p) }}</td>
                  <td>{{ labelTipoParticipante(p.tipo) }}</td>
                  <td class="text-center">
                    <v-chip
                      v-if="
                        p.tipo === 'aluno' &&
                        p.matricula &&
                        p.matricula.aluno &&
                        p.matricula.aluno.especial
                      "
                      x-small
                      color="blue"
                      dark
                      >PcD</v-chip
                    >
                    <span v-else>- - -</span>
                  </td>
                  <td class="text-center">
                    <v-chip x-small :color="p.presente ? 'green' : 'red'" dark>
                      {{ p.presente ? "Presente" : "Ausente" }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="amber darken-2" dark @click="dialogVisualizar = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Certificado -->
    <v-dialog v-model="dialogCertificado" max-width="480">
      <v-card>
        <v-card-title class="primary white--text">Anexar certificado</v-card-title>
        <v-card-text class="pt-4">
          <v-file-input
            v-model="certificadoFile"
            label="Certificado (PDF ou imagem)"
            accept=".pdf,image/*"
            outlined
            dense
            prepend-icon="fa fa-paperclip"
            show-size
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCertificado = false">Cancelar</v-btn>
          <v-btn
            :color="certificadoFile ? 'primary' : 'grey lighten-1'"
            :dark="!!certificadoFile"
            :disabled="!certificadoFile"
            :loading="salvandoCertificado"
            @click="salvarCertificado"
          >
            Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
export default {
  name: "AtendimentosPage",

  data() {
    return {
      isLoading: false,
      search: "",
      atendimentos: [],
      dialogVisualizar: false,
      detalhe: null,
      dialogCertificado: false,
      certificadoFile: null,
      salvandoCertificado: false,
      atendimentoCertificadoId: null,
      tableHeaders: [
        { text: "Data", value: "data_formatada" },
        { text: "Tipo", value: "tipo" },
        { text: "Prof. Orientador", value: "professor_orientador" },
        { text: "Público", value: "publico_alvo", sortable: false },
        { text: "Participantes", value: "participantes_count" },
        { text: "Frequência", value: "presencas_confirmadas" },
        { text: "Situação", value: "situacao" },
        { text: "Ações", value: "actions", sortable: false, align: "end" },
      ],
    };
  },

  mounted() {
    this.listar();
  },

  methods: {
    async listar() {
      this.isLoading = true;
      try {
        this.atendimentos = await this.$services.atendimentosService.syncAll(this.search || null);
      } catch (error) {
        this.$handleError(error);
      }
      this.isLoading = false;
    },

    capitalize(v) {
      return v ? v.charAt(0).toUpperCase() + v.slice(1) : "";
    },

    labelTipoParticipante(tipo) {
      return { aluno: "Aluno", pai: "Pai/Responsável", funcionario: "Funcionário" }[tipo] || tipo;
    },

    nomeParticipante(p) {
      if (p.tipo === "aluno") {
        return p.matricula && p.matricula.aluno ? p.matricula.aluno.nomecompleto : "Aluno";
      }
      return p.nome_completo;
    },

    podeEditar(item) {
      return (
        item.situacao !== "Confirmado" || this.$can(["atendimentos.confirmar", "atendimentos.*"])
      );
    },

    criar() {
      this.$router.push({ name: "diarioAtendimentos.atendimentos.create" });
    },

    editar(atendimento) {
      this.$router.push({
        name: "diarioAtendimentos.atendimentos.edit",
        params: { atendimento_id: atendimento.id },
      });
    },

    frequenciaManual(atendimento) {
      this.$router.push({
        name: "diarioAtendimentos.atendimentos.edit",
        params: { atendimento_id: atendimento.id },
        query: { tab: "frequencia" },
      });
    },

    async visualizar(atendimento) {
      this.$loaderService.open("Carregando atendimento...");
      try {
        this.detalhe = await this.$services.atendimentosService.find(atendimento.id);
        this.dialogVisualizar = true;
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },

    async imprimir(atendimento) {
      this.$loaderService.open("Gerando PDF...");
      try {
        await this.$services.atendimentosService.imprimir(atendimento.id);
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },

    confirmar(atendimento) {
      this.$modals.warn({
        buttons: [
          {
            callback: () => this.$modals.close(),
            props: { color: "warning", small: true },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Confirmando atendimento...");
              try {
                await this.$services.atendimentosService.confirmar(atendimento.id);
                this.$toast.success("Atendimento confirmado com sucesso");
                await this.listar();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            props: { color: "success", small: true },
            text: "Confirmar",
          },
        ],
        message:
          "Após confirmar, o professor orientador não poderá mais editar este atendimento. Deseja continuar?",
        title: "Confirmar atendimento",
      });
    },

    abrirCertificado(atendimento) {
      this.atendimentoCertificadoId = atendimento.id;
      this.certificadoFile = null;
      this.dialogCertificado = true;
    },

    async salvarCertificado() {
      this.salvandoCertificado = true;
      try {
        await this.$services.atendimentosService.anexarCertificado(
          this.atendimentoCertificadoId,
          this.certificadoFile
        );
        this.$toast.success("Certificado anexado com sucesso");
        this.dialogCertificado = false;
        await this.listar();
      } catch (error) {
        this.$handleError(error);
      }
      this.salvandoCertificado = false;
    },

    async baixarCertificado(atendimento) {
      this.$loaderService.open("Baixando certificado...");
      try {
        await this.$services.atendimentosService.baixarCertificado(atendimento.id);
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },

    deletar(atendimento) {
      this.$modals.danger({
        buttons: [
          {
            callback: () => this.$modals.close(),
            props: { color: "warning", small: true },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Deletando atendimento");
              try {
                await this.$services.atendimentosService.deletar(atendimento);
                this.$toast.success("Atendimento deletado com sucesso");
                await this.listar();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            disableIfCodeDoesntMatch: true,
            props: { color: "error", small: true },
            text: "Confirmar",
          },
        ],
        confirmationCode: String(atendimento.id),
        confirmationMessage: `Por favor, digite <strong>${atendimento.id}</strong> para confirmar`,
        message: "Tem certeza que deseja apagar este atendimento?",
        title: "Cuidado!",
      });
    },
  },
};
</script>

<style scoped></style>
